import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { getApiBaseUrl } from '@/utils/platform'

class ApiClient {
  private client: AxiosInstance
  private csrfToken: string | null = null

  constructor() {
    const baseURL = getApiBaseUrl()

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Required for CSRF token cookies
    })

    this.setupInterceptors()
    // Fetch CSRF token on initialization (non-blocking)
    this.fetchCsrfToken().catch(() => {
      // Silently fail - CSRF is optional for Bearer token auth
      console.warn('CSRF token fetch failed on initialization - this is OK for Bearer token auth')
    })
  }

  // Fetch CSRF token from server
  private async fetchCsrfToken() {
    try {
      // Use /api/csrf-token to match nginx routing (all /api routes go to backend)
      const url = `${this.client.defaults.baseURL}/api/csrf-token`
      console.log('Fetching CSRF token from:', url)
      const response = await fetch(url, {
        credentials: 'include',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      })
      if (!response.ok) {
        throw new Error(`CSRF token fetch failed: ${response.status} ${response.statusText}`)
      }
      const data = await response.json()
      if (data.success && data.csrfToken) {
        this.csrfToken = data.csrfToken
        console.log('CSRF token fetched successfully')
      }
    } catch (error) {
      console.warn('Failed to fetch CSRF token:', error)
      // CSRF token fetch failure is not critical for API endpoints using Bearer tokens
    }
  }

  private setupInterceptors() {
    // Request interceptor - add auth token and CSRF token
    this.client.interceptors.request.use(
      async (config) => {
        const token = useAuthStore.getState().accessToken
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Add CSRF token for state-changing requests
        // Note: API endpoints use Bearer tokens, so CSRF is not strictly required,
        // but we add it for defense in depth and for any non-Bearer endpoints
        // Skip CSRF token for login endpoint to avoid blocking
        if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '') &&
          !config.url?.includes('/api/auth/login')) {
          // Refresh CSRF token if not available (non-blocking)
          if (!this.csrfToken) {
            // Don't await - fetch in background to avoid blocking login
            this.fetchCsrfToken().catch(() => {
              // Silently fail - CSRF is optional for Bearer token auth
            })
          }
          if (this.csrfToken) {
            config.headers['X-CSRF-Token'] = this.csrfToken
          }
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor - handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // Enhanced error logging
        if (error.config?.url?.includes('/api/auth/login')) {
          console.error('Login API error:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
          })
        }

        // Handle network errors (backend not reachable)
        if (!error.response) {
          console.error('Network error - backend may not be running:', {
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            message: error.message,
            code: error.code,
          })
          return Promise.reject(new Error('Backend server is not reachable. Please ensure the backend is running on http://localhost:3001'))
        }

        // Prevent infinite loop if refresh endpoint itself returns 401
        if (originalRequest.url?.includes("/api/auth/refresh")) {
          return Promise.reject(error)
        }

        // Skip token refresh for auth endpoints (login, logout) - they don't need refresh
        const isAuthEndpoint = originalRequest.url?.includes('/api/auth/login') ||
          originalRequest.url?.includes('/api/auth/logout')

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true

          try {
            await useAuthStore.getState().refreshAuth()
            const newToken = useAuthStore.getState().accessToken
            originalRequest.headers.Authorization = `Bearer ${newToken}`

            // Refresh CSRF token after re-authentication
            await this.fetchCsrfToken()
            if (this.csrfToken && ['post', 'put', 'patch', 'delete'].includes(originalRequest.method?.toLowerCase() || '')) {
              originalRequest.headers['X-CSRF-Token'] = this.csrfToken
            }

            return this.client(originalRequest)
          } catch (refreshError) {
            useAuthStore.getState().logout()
            window.location.href = '/login'
            return Promise.reject(refreshError)
          }
        }

        // Handle CSRF token errors (403)
        if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID') {
          // Refresh CSRF token and retry
          await this.fetchCsrfToken()
          if (this.csrfToken && originalRequest.headers) {
            originalRequest.headers['X-CSRF-Token'] = this.csrfToken
            return this.client(originalRequest)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Generic request method
  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    try {
      console.log('API Request:', {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL || this.client.defaults.baseURL,
        fullUrl: `${config.baseURL || this.client.defaults.baseURL}${config.url}`,
      })
      const response = await this.client(config)
      console.log('API Response:', {
        status: response.status,
        url: config.url,
      })
      return response.data
    } catch (error: any) {
      console.error('API Request Error:', {
        url: config.url,
        method: config.method,
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
      })
      throw error
    }
  }

  // HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'GET', url })
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'POST', url, data })
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'PUT', url, data })
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'PATCH', url, data })
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'DELETE', url })
  }

  // File upload
  async uploadFile<T = any>(url: string, file: File, config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    return this.request({
      ...config,
      method: 'POST',
      url,
      data: formData,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  // Task endpoints
  async createTask(data: {
    title: string
    description?: string
    priority?: string
    dueDate?: string
    departmentId: string
    assigneeId?: string
  }) {
    return this.post<{ success: boolean; message: string; data: any }>('/api/tasks', data)
  }

  async updateTask(id: string, data: {
    title?: string
    description?: string
    priority?: string
    dueDate?: string | null
    departmentId?: string
    assigneeId?: string | null
  }) {
    return this.patch<{ success: boolean; message: string; data: any }>(`/api/tasks/${id}`, data)
  }

  async getTaskDepartments() {
    return this.get<{ success: boolean; data: { id: string; name: string; type: string; headId: string | null }[] }>(
      '/api/tasks/departments'
    )
  }

  async getTaskDepartmentMembers(departmentId: string, q?: string) {
    const params = new URLSearchParams()
    if (q?.trim()) params.append('q', q.trim())
    const qs = params.toString()
    const url = `/api/tasks/departments/${departmentId}/members${qs ? `?${qs}` : ''}`
    return this.get<{ success: boolean; data: import('@/types').TaskDepartmentMember[] }>(url)
  }

  async getTasksSummaryByDepartment() {
    return this.get<{ success: boolean; data: import('@/types').TaskDepartmentSummaryRow[] }>(
      '/api/tasks/summary-by-department'
    )
  }

  async deleteTask(id: string) {
    return this.delete<{ success: boolean; message: string }>(`/api/tasks/${id}`)
  }

  async getTasksAssignedBy(filters?: {
    status?: string
    priority?: string
    departmentId?: string
    completed?: boolean
    overdue?: boolean
  }) {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.priority) params.append('priority', filters.priority)
    if (filters?.departmentId) params.append('departmentId', filters.departmentId)
    if (filters?.completed === true) params.append('completed', 'true')
    if (filters?.completed === false) params.append('completed', 'false')
    if (filters?.overdue === true) params.append('overdue', 'true')
    if (filters?.overdue === false) params.append('overdue', 'false')
    const url = `/api/tasks/assigned-by?${params.toString()}`
    return this.get<{ success: boolean; data: any[] }>(url)
  }

  async getTasksAssignedTo(filters?: {
    status?: string
    priority?: string
    departmentId?: string
    completed?: boolean
    overdue?: boolean
  }) {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.priority) params.append('priority', filters.priority)
    if (filters?.departmentId) params.append('departmentId', filters.departmentId)
    if (filters?.completed === true) params.append('completed', 'true')
    if (filters?.completed === false) params.append('completed', 'false')
    if (filters?.overdue === true) params.append('overdue', 'true')
    if (filters?.overdue === false) params.append('overdue', 'false')
    const url = `/api/tasks/assigned-to?${params.toString()}`
    return this.get<{ success: boolean; data: any[] }>(url)
  }

  async getAllTasks(filters?: {
    status?: string
    priority?: string
    departmentId?: string
    completed?: boolean
    overdue?: boolean
    assignerId?: string
    assigneeId?: string
    dueDateFrom?: string
    dueDateTo?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.priority) params.append('priority', filters.priority)
    if (filters?.departmentId) params.append('departmentId', filters.departmentId)
    if (filters?.completed === true) params.append('completed', 'true')
    if (filters?.completed === false) params.append('completed', 'false')
    if (filters?.overdue === true) params.append('overdue', 'true')
    if (filters?.overdue === false) params.append('overdue', 'false')
    if (filters?.assignerId) params.append('assignerId', filters.assignerId)
    if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId)
    if (filters?.dueDateFrom) params.append('dueDateFrom', filters.dueDateFrom)
    if (filters?.dueDateTo) params.append('dueDateTo', filters.dueDateTo)
    return this.get<{ success: boolean; data: any[] }>(`/api/tasks?${params.toString()}`)
  }

  async getTaskById(id: string) {
    return this.get<{ success: boolean; data: any }>(`/api/tasks/${id}`)
  }

  async updateTaskStatus(id: string, data: { status: string; completionNote?: string }) {
    return this.patch<{ success: boolean; message: string; data: any }>(`/api/tasks/${id}/status`, data)
  }

  // Route optimization endpoints
  async optimizeRoute(data: {
    vehicleId: string
    vehicleRouteId?: string
    nodes: Array<{
      id: string
      position: [number, number]
      priority?: number
      metadata?: Record<string, any>
    }>
    startPosition: [number, number]
    vehicle?: {
      id?: string
      fuelType?: 'gasoline' | 'diesel' | 'electric' | 'hybrid'
      fuelCapacity?: number
      averageSpeed?: number
      fuelConsumptionRate?: number
    }
    options?: {
      algorithm?: 'nearest_neighbor' | 'genetic' | 'ant_colony' | 'hybrid'
      maxIterations?: number
      populationSize?: number
      mutationRate?: number
      timeLimitMs?: number
      priorityWeight?: number
      fuelOptimization?: boolean
    }
    saveToDatabase?: boolean
  }) {
    return this.post<{
      success: boolean
      data: {
        optimizationId?: string
        best: {
          sequence: Array<{
            id: string
            position: [number, number]
            priority: number
            metadata?: Record<string, any>
          }>
          totalDistance: number
          totalTime: number
          fuelCost: number
          efficiency: number
        }
        metadata: {
          algorithm: string
          pattern: string
          optimizationTimeMs: number
          parameters: any
          savings: {
            distance: {
              original?: number
              optimized: number
              saved?: number
              savedPercent?: number
            }
            time: {
              original?: number
              optimized: number
              saved?: number
              savedPercent?: number
            }
            fuel: {
              original?: number
              optimized: number
              saved?: number
              savedPercent?: number
            }
          }
        }
      }
    }>('/api/routes/optimize', data)
  }

  async getOptimizationHistory(vehicleId?: string, limit?: number) {
    const params = new URLSearchParams()
    if (vehicleId) params.append('vehicleId', vehicleId)
    if (limit) params.append('limit', limit.toString())
    return this.get<{
      success: boolean
      data: Array<any>
    }>(`/api/routes/optimize/history?${params.toString()}`)
  }

  async getOptimizationById(id: string) {
    return this.get<{
      success: boolean
      data: any
    }>(`/api/routes/optimize/${id}`)
  }

  async markOptimizationAsApplied(id: string) {
    return this.patch<{
      success: boolean
      data: any
    }>(`/api/routes/optimize/${id}/apply`)
  }

  async getOptimizationStats(vehicleId?: string) {
    const params = new URLSearchParams()
    if (vehicleId) params.append('vehicleId', vehicleId)
    return this.get<{
      success: boolean
      data: {
        totalOptimizations: number
        appliedOptimizations: number
        avgDistanceSavingsPercent: number
        avgTimeSavingsPercent: number
        avgFuelSavingsPercent: number
      }
    }>(`/api/routes/optimize/stats?${params.toString()}`)
  }

  async getAvailableAlgorithms() {
    return this.get<{
      success: boolean
      data: {
        algorithms: Array<{
          id: string
          name: string
          description: string
          complexity: string
          recommended: string
          speed: string
        }>
      }
    }>('/api/routes/optimize/algorithms')
  }

  // Field dispatch (multi-vehicle + geo jobs)
  async createDispatchJob(data: {
    title: string
    latitude: number
    longitude: number
    priority?: number
    description?: string
  }) {
    return this.post<{ success: boolean; data: any }>('/api/dispatch/jobs', data)
  }

  async getDispatchJobs(params?: {
    status?: string
    page?: number
    limit?: number
    sort?: 'createdAt_desc' | 'createdAt_asc' | 'priority_desc'
  }) {
    const q = new URLSearchParams()
    if (params?.status) q.append('status', params.status)
    if (params?.sort) q.append('sort', params.sort)
    if (params?.page) q.append('page', String(params.page))
    if (params?.limit) q.append('limit', String(params.limit))
    const qs = q.toString()
    return this.get<{
      success: boolean
      data: any[]
      pagination: { page: number; limit: number; total: number; totalPages: number }
    }>(`/api/dispatch/jobs${qs ? `?${qs}` : ''}`)
  }

  async getDispatchJob(id: string) {
    return this.get<{ success: boolean; data: any }>(`/api/dispatch/jobs/${id}`)
  }

  async updateDispatchJob(
    id: string,
    data: Partial<{ title: string; description: string; latitude: number; longitude: number; priority: number }>
  ) {
    return this.patch<{ success: boolean; data: any }>(`/api/dispatch/jobs/${id}`, data)
  }

  async cancelDispatchJob(id: string) {
    return this.delete<{ success: boolean; data: any }>(`/api/dispatch/jobs/${id}`)
  }

  async runDispatch(data?: {
    jobIds?: string[]
    vehicleIds?: string[]
    maxStopsPerVehicle?: number
    algorithm?: 'nearest_neighbor' | 'genetic' | 'ant_colony' | 'hybrid'
  }) {
    return this.post<{
      success: boolean
      data: {
        planId: string | null
        status: string
        vehicleResults: Array<{
          vehicleId: string
          plateNumber: string
          jobCount: number
          distance?: number
          time?: number
          fuel?: number
          error?: string
        }>
        warnings: string[]
        explanation?: {
          overview: string[]
          perVehicle: Array<{ plateNumber: string; lines: string[] }>
        }
      }
      error?: string
    }>('/api/dispatch/run', data ?? {})
  }

  async getDispatchPlans(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams()
    if (params?.page) q.append('page', String(params.page))
    if (params?.limit) q.append('limit', String(params.limit))
    const qs = q.toString()
    return this.get<{
      success: boolean
      data: any[]
      pagination: { page: number; limit: number; total: number; totalPages: number }
    }>(`/api/dispatch/plans${qs ? `?${qs}` : ''}`)
  }

  async getDispatchPlan(id: string) {
    return this.get<{ success: boolean; data: any }>(`/api/dispatch/plans/${id}`)
  }

  // WhatsApp endpoints
  async resetWhatsAppConnection() {
    return this.post<{
      success: boolean
      message: string
    }>('/api/beyaz-masa/whatsapp/reset')
  }

  // Yazı İşleri Evrak
  async getYaziIsleriDepartments() {
    return this.get<{
      success: boolean
      data: { id: string; name: string; type: string }[]
    }>('/api/yazi-isleri/departments')
  }

  async getYaziIsleriDepartmentMembers(departmentId: string, q?: string) {
    const params = new URLSearchParams()
    if (q?.trim()) params.append('q', q.trim())
    const qs = params.toString()
    return this.get<{
      success: boolean
      data: import('@/types').TaskDepartmentMember[]
    }>(`/api/yazi-isleri/departments/${departmentId}/members${qs ? `?${qs}` : ''}`)
  }

  async getYaziIsleriEvraklar(filters?: {
    status?: string
    departmentId?: string
    targetUserId?: string
    q?: string
    gelisTarihiFrom?: string
    gelisTarihiTo?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.departmentId) params.append('departmentId', filters.departmentId)
    if (filters?.targetUserId) params.append('targetUserId', filters.targetUserId)
    if (filters?.q?.trim()) params.append('q', filters.q.trim())
    if (filters?.gelisTarihiFrom) params.append('gelisTarihiFrom', filters.gelisTarihiFrom)
    if (filters?.gelisTarihiTo) params.append('gelisTarihiTo', filters.gelisTarihiTo)
    const qs = params.toString()
    return this.get<{
      success: boolean
      data: import('@/types').YaziIsleriEvrak[]
    }>(`/api/yazi-isleri${qs ? `?${qs}` : ''}`)
  }

  async getYaziIsleriEvrakById(id: string) {
    return this.get<{
      success: boolean
      data: import('@/types').YaziIsleriEvrak
    }>(`/api/yazi-isleri/${id}`)
  }

  async createYaziIsleriEvrak(data: import('@/types').CreateYaziIsleriEvrakInput) {
    return this.post<{
      success: boolean
      message?: string
      data: import('@/types').YaziIsleriEvrak
    }>('/api/yazi-isleri', data)
  }

  async updateYaziIsleriEvrak(id: string, data: import('@/types').UpdateYaziIsleriEvrakInput) {
    return this.patch<{
      success: boolean
      message?: string
      data: import('@/types').YaziIsleriEvrak
    }>(`/api/yazi-isleri/${id}`, data)
  }
}

export const apiClient = new ApiClient()

// Default export for convenience
export default apiClient
